/**
 * AAAprinters - Designs Gallery Component
 * Displays user's AI-generated designs in a beautiful grid
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Palette, Download, Trash2, Heart, Loader2, ShoppingBag, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import type { UserDesign } from "@/hooks/useUserDesigns";

interface DesignsGalleryProps {
  designs: UserDesign[];
  loading: boolean;
  onDelete: (id: string) => Promise<void>;
  onToggleFavorite: (id: string) => Promise<void>;
}

export function DesignsGallery({
  designs,
  loading,
  onDelete,
  onToggleFavorite,
}: DesignsGalleryProps) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteClick = (id: string) => {
    setDesignToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!designToDelete) return;

    setDeletingId(designToDelete);
    try {
      await onDelete(designToDelete);
      toast.success("Design deleted successfully");
    } catch (error) {
      toast.error("Failed to delete design");
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setDesignToDelete(null);
    }
  };

  const handleDownload = async (design: UserDesign) => {
    try {
      const response = await fetch(design.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${design.design_name || 'design'}_${design.id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Design downloaded!");
    } catch (error) {
      toast.error("Failed to download design");
    }
  };

  const handleToggleFavorite = async (id: string) => {
    try {
      await onToggleFavorite(id);
    } catch (error) {
      toast.error("Failed to update favorite status");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (designs.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My Designs</CardTitle>
          <CardDescription>Your AI-generated creations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-brand-cyan via-brand-magenta to-brand-yellow mb-4">
              <Palette className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold mb-2">No designs yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first AI-powered design in our Design Studio
            </p>
            <Button onClick={() => navigate("/design-studio")}>
              <Palette className="mr-2 h-4 w-4" />
              Open Design Studio
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>My Designs</CardTitle>
          <CardDescription>
            You have {designs.length} design{designs.length !== 1 ? 's' : ''}
            {designs.filter(d => d.is_favorite).length > 0 && (
              <span> ({designs.filter(d => d.is_favorite).length} favorite{designs.filter(d => d.is_favorite).length !== 1 ? 's' : ''})</span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {designs.map((design) => (
              <div
                key={design.id}
                className="group relative border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 hover:border-primary/50"
              >
                {/* Design Image */}
                <div className="aspect-square bg-muted relative overflow-hidden">
                  <img
                    src={design.image_url}
                    alt={design.design_name || "Design"}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />

                  {/* Favorite Badge */}
                  {design.is_favorite && (
                    <Badge className="absolute top-2 right-2 bg-red-500 text-white border-0">
                      <Heart className="h-3 w-3 fill-current" />
                    </Badge>
                  )}

                  {/* Applied Badge */}
                  {design.is_applied_to_product && (
                    <Badge className="absolute top-2 left-2 bg-green-500 text-white border-0">
                      <ShoppingBag className="h-3 w-3" />
                    </Badge>
                  )}

                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-2">
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => handleToggleFavorite(design.id)}
                      title={design.is_favorite ? "Remove from favorites" : "Add to favorites"}
                    >
                      <Heart className={`h-4 w-4 ${design.is_favorite ? 'fill-current text-red-500' : ''}`} />
                    </Button>
                    <Button
                      size="icon"
                      variant="secondary"
                      onClick={() => handleDownload(design)}
                      title="Download design"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => handleDeleteClick(design.id)}
                      disabled={deletingId === design.id}
                      title="Delete design"
                    >
                      {deletingId === design.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Design Info */}
                <div className="p-3">
                  <h4 className="font-semibold text-sm truncate mb-1">
                    {design.design_name || "Untitled Design"}
                  </h4>
                  {design.prompt && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {design.prompt}
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {new Date(design.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Design</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this design? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
